/**
 * Enhanced GA Tracking – Context and hook
 * Provides tracking functions to components. No-op when enhancedTracking.enabled is false.
 */

import React, {createContext, useContext, useLayoutEffect} from 'react';
import * as track from './tracking';

const TrackingContext = createContext(null);

/**
 * Provider that initializes tracking when settings are available.
 * Wrap app/routes with this when settings are loaded.
 *
 * Init uses useLayoutEffect (not useEffect) so track.init runs before any
 * descendant useEffect. Child effects run before parent useEffect, so init in
 * useEffect would run after useLinkTracking’s effect — shouldTrack() stayed
 * false and the request-rep listener never attached.
 */
export const TrackingProvider = ({settings, children}) => {
  useLayoutEffect(() => {
    if (settings) {
      track.init(settings);
    }
  }, [settings]);

  const enabled = Boolean(settings?.enhancedTracking?.enabled);
  const value = enabled
    ? {
      enhancedTrackingEnabled: true,
      trackResultClick: track.resultClick,
      trackSimpleBrochureClick: track.simpleBrochureClick,
      trackRequestRepClick: track.requestRepClick,
      trackExternalLinkExitModal: track.externalLinkExitModal,
      filtersApply: track.filtersApply,
      buildFiltersApplyParams: track.buildFiltersApplyParams,
      shouldTrack: track.shouldTrack
    }
    : {
      enhancedTrackingEnabled: false,
      trackResultClick: () => {},
      trackSimpleBrochureClick: () => {},
      trackRequestRepClick: () => {},
      trackExternalLinkExitModal: () => {},
      filtersApply: () => {},
      buildFiltersApplyParams: () => ({}),
      shouldTrack: () => false
    };

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
}

/**
 * Hook to access tracking functions.
 * Returns no-op functions when enhanced tracking is disabled.
 */
export const useTracking = () => {
  const context = useContext(TrackingContext);
  if (!context) {
    return {
      enhancedTrackingEnabled: false,
      trackResultClick: () => {},
      trackSimpleBrochureClick: () => {},
      trackRequestRepClick: () => {},
      trackExternalLinkExitModal: () => {},
      filtersApply: () => {},
      buildFiltersApplyParams: () => ({}),
      shouldTrack: () => false
    };
  }
  return context;
};
